import { Loader2, CheckCircle2, XCircle, Ban, ExternalLink, PenLine } from "lucide-react";
import { txUrl } from "../lib/chains";

const MAP = {
  awaiting_signature: { icon: PenLine, tone: "text-warning", label: "Confirm in your wallet…" },
  pending: { icon: Loader2, tone: "text-lime", label: "Submitted — waiting for on-chain confirmation", spin: true },
  confirmed: { icon: CheckCircle2, tone: "text-lime", label: "Confirmed on-chain" },
  failed: { icon: XCircle, tone: "text-danger", label: "Transaction failed (reverted on-chain)" },
  rejected: { icon: Ban, tone: "text-slate-400", label: "Rejected in wallet — nothing was sent" },
  error: { icon: XCircle, tone: "text-danger", label: "Could not submit transaction" },
};

export const TxStatus = ({ tx, testid = "tx-status" }) => {
  if (!tx) return null;
  const m = MAP[tx.stage];
  const Icon = m.icon;
  return (
    <div data-testid={testid} data-stage={tx.stage} className="rounded-lg border border-white/10 bg-black/30 p-3 text-xs">
      <p className={`flex items-center gap-2 font-semibold ${m.tone}`}>
        <Icon className={`h-4 w-4 ${m.spin ? "animate-spin" : ""}`} /> {tx.label ? `${tx.label}: ` : ""}{m.label}
      </p>
      {tx.error && <p className="mt-1 text-slate-400 break-words">{tx.error}</p>}
      {tx.hash && (
        <a data-testid={`${testid}-explorer-link`} href={txUrl(tx.hash, tx.chainId)} target="_blank" rel="noreferrer" className="mt-1.5 inline-flex items-center gap-1 font-mono text-lime hover:underline">
          {tx.hash.slice(0, 18)}… <ExternalLink className="h-3 w-3" />
        </a>
      )}
    </div>
  );
};

export const isRejection = (e) => e?.name === "UserRejectedRequestError" || e?.cause?.name === "UserRejectedRequestError" || e?.code === 4001 || /user rejected|denied|rejected the request/i.test(e?.shortMessage || e?.message || "");

const friendly = (e) => {
  const m = e?.shortMessage || e?.message || "Unknown error";
  if (/insufficient funds/i.test(m)) return "Not enough ETH to cover the amount plus network gas.";
  if (/nonce/i.test(m)) return "Wallet nonce conflict — wait for pending transactions, then retry.";
  if (/chain mismatch|does not match the target chain/i.test(m)) return "Your wallet is on a different network — switch network and retry.";
  return m;
};

export async function runTx({ send, pc, setTx, label, chainId }) {
  setTx({ stage: "awaiting_signature", label, chainId });
  let hash;
  try {
    hash = await send();
  } catch (e) {
    setTx(isRejection(e) ? { stage: "rejected", label } : { stage: "error", label, chainId, error: friendly(e) });
    return null;
  }
  setTx({ stage: "pending", label, hash, chainId });
  try {
    const rc = await pc.waitForTransactionReceipt({ hash, timeout: 180000 });
    setTx({ stage: rc.status === "success" ? "confirmed" : "failed", label, hash, chainId });
    return rc.status === "success" ? rc : null;
  } catch (e) {
    setTx({ stage: "error", label, hash, chainId, error: `Receipt not found yet: ${e.shortMessage || e.message}. Check the explorer.` });
    return null;
  }
}
